// Bridge to the EasyLister Chrome extension.
//
// The extension's `panelBridgeMain.ts` (world: "MAIN") patches
// `Storage.prototype.setItem/removeItem` on pages matching localhost,
// 127.0.0.1, and the production panel domain, and forwards writes to
// `panelBridge.ts` (isolated world), which mirrors them into
// `chrome.storage.local` — and vice versa, dispatching a `cde-bridge-sync`
// window event whenever the extension's storage changes.
//
// This module is the panel-side half of that contract: same localStorage
// keys, same shapes. No chrome.runtime messaging is involved — plain
// localStorage reads/writes are all that's needed, the extension's content
// scripts do the rest (no-op harmlessly if the extension isn't installed).
import { useCallback, useEffect, useState } from "react";

const PRODUCTS_KEY = "clonedProducts";
const SELECTED_KEY = "cde_selectedProductId";
const AUTH_KEY = "cde_auth";
const SESSION_KEY = "cde_session";
const SCANS_KEY = "priceManagerScans";

const BRIDGE_SYNC_EVENT = "cde-bridge-sync";

const readJSON = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

export const getClonedProducts = () => readJSON(PRODUCTS_KEY, []);

export const setClonedProducts = (products) => {
  localStorage.setItem(PRODUCTS_KEY, JSON.stringify(products));
};

export const deleteClonedProduct = (id) => {
  const next = getClonedProducts().filter((p) => p.id !== id);
  setClonedProducts(next);
  if (localStorage.getItem(SELECTED_KEY) === id) {
    localStorage.removeItem(SELECTED_KEY);
  }
  return next;
};

export const getSelectedProductId = () => localStorage.getItem(SELECTED_KEY);

export const setSelectedProductId = (id) => {
  if (id === null) localStorage.removeItem(SELECTED_KEY);
  else localStorage.setItem(SELECTED_KEY, id);
};

// Mirrors the logged-in session into the extension's expected keys so the
// popup unlocks automatically — written alongside (not instead of) the
// panel's own token storage.
export const writeExtensionAuth = (user, { accessToken, refreshToken } = {}) => {
  if (!user) return;
  localStorage.setItem(
    AUTH_KEY,
    JSON.stringify({
      id: user.id,
      email: user.email,
      name: user.name,
      profileImage: user.profileImage || "",
      authProvider: user.authProvider,
      isEmailVerified: user.isEmailVerified,
      status: user.status,
    }),
  );
  if (accessToken && refreshToken) {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ accessToken, refreshToken }));
  }
};

export const clearExtensionAuth = () => {
  localStorage.removeItem(AUTH_KEY);
  localStorage.removeItem(SESSION_KEY);
};

/** Live-updating list of products cloned via the extension. */
export const useClonedProducts = () => {
  const [products, setProducts] = useState(() => getClonedProducts());

  const refresh = useCallback(() => setProducts(getClonedProducts()), []);

  useEffect(() => {
    window.addEventListener(BRIDGE_SYNC_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(BRIDGE_SYNC_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  const remove = useCallback(
    (id) => {
      const next = deleteClonedProduct(id);
      setProducts(next);
    },
    [],
  );

  const selectForAutofill = useCallback((id) => {
    setSelectedProductId(id);
  }, []);

  return { products, refresh, remove, selectForAutofill };
};

// ─── Price Manager scans ────────────────────────────────────────────────────
// The extension's only job here is scanning the Meesho Inventory page and
// writing the result to `priceManagerScans` (chrome.storage.local, bridged to
// this localStorage key). Everything else — viewing, editing, search/filter,
// Auto WDRP, bulk actions — lives entirely in the panel below.

export const SCAN_EXPIRY_MS = 48 * 60 * 60 * 1000; // 48 hours

export const getInventoryScans = () => readJSON(SCANS_KEY, []);

export const setInventoryScans = (scans) => {
  localStorage.setItem(SCANS_KEY, JSON.stringify(scans));
};

export const deleteInventoryScan = (id) => {
  const next = getInventoryScans().filter((s) => s.id !== id);
  setInventoryScans(next);
  return next;
};

export const updateScanProducts = (id, products) => {
  const next = getInventoryScans().map((s) => (s.id === id ? { ...s, products } : s));
  setInventoryScans(next);
  return next;
};

export const updateScanSettings = (id, patch) => {
  const next = getInventoryScans().map((s) => (s.id === id ? { ...s, ...patch } : s));
  setInventoryScans(next);
  return next;
};

export const isScanExpired = (scan) => Date.now() - new Date(scan.scannedAt).getTime() > SCAN_EXPIRY_MS;

export const msUntilScanExpiry = (scan) => SCAN_EXPIRY_MS - (Date.now() - new Date(scan.scannedAt).getTime());

/** "Just now" / "10 minutes ago" / "2 hours ago" */
export const formatRelativeTime = (iso) => {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? "" : "s"} ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? "" : "s"} ago`;
  const diffDay = Math.floor(diffHr / 24);
  return `${diffDay} day${diffDay === 1 ? "" : "s"} ago`;
};

/** "Expiring in 48 hrs" / "Expiring in 12 hrs" / "Expired" */
export const formatScanExpiration = (scan) => {
  const remainingMs = msUntilScanExpiry(scan);
  if (remainingMs <= 0) return "Expired";
  return `Expiring in ${Math.ceil(remainingMs / (60 * 60 * 1000))} hrs`;
};

/** Live-updating list of Price Manager scans, synced from the extension. */
export const useInventoryScans = () => {
  const [scans, setScans] = useState(() => getInventoryScans());

  const refresh = useCallback(() => setScans(getInventoryScans()), []);

  useEffect(() => {
    window.addEventListener(BRIDGE_SYNC_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(BRIDGE_SYNC_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  const deleteScan = useCallback((id) => {
    setScans(deleteInventoryScan(id));
  }, []);

  const saveProducts = useCallback((id, products) => {
    setScans(updateScanProducts(id, products));
  }, []);

  const saveSettings = useCallback((id, patch) => {
    setScans(updateScanSettings(id, patch));
  }, []);

  return { scans, refresh, deleteScan, saveProducts, saveSettings };
};
